import { Suspense, lazy, useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuth } from './state/AuthContext';
import AppShell from './components/AppShell';
import Landing from './pages/Landing';
import AuthPage from './pages/AuthPage';
import EnrollTwoFactor from './pages/auth/EnrollTwoFactor';
import LegalPage from './pages/LegalPage';
import KiteCallback from './pages/KiteCallback';

const Dashboard = lazy(() => import('./pages/app/Dashboard'));
const Portfolio = lazy(() => import('./pages/app/Portfolio'));
const Insights = lazy(() => import('./pages/app/Insights'));
const Markets = lazy(() => import('./pages/app/Markets'));
const Learn = lazy(() => import('./pages/app/Learn'));
const Research = lazy(() => import('./pages/app/Research'));
const Plans = lazy(() => import('./pages/app/Plans'));
const PlanComparison = lazy(() => import('./pages/app/PlanComparison'));
const Founder = lazy(() => import('./pages/app/Founder'));
const SettingsPage = lazy(() => import('./pages/app/Settings'));
const Household = lazy(() => import('./pages/app/Household'));
const Options = lazy(() => import('./pages/app/Options'));
const Advisory = lazy(() => import('./pages/app/Advisory'));
const Robo = lazy(() => import('./pages/app/Robo'));
const Execution = lazy(() => import('./pages/app/Execution'));
const Aggregation = lazy(() => import('./pages/app/Aggregation'));
const Wellness = lazy(() => import('./pages/app/Wellness'));
const Privacy = lazy(() => import('./pages/app/Privacy'));
const Ai = lazy(() => import('./pages/app/Ai'));
const Waiting = lazy(() => import('./pages/app/Waiting'));
const Budgets = lazy(() => import('./pages/app/Budgets'));
const VerifyProof = lazy(() => import('./pages/VerifyProof'));
// Public SEO surface — un-gated, no session. Lazy so the marketing landing page
// (which most visitors see first) does not pay for a calculator it never opens.
const Calculators = lazy(() => import('./pages/Calculators'));
const CalculatorPage = lazy(() => import('./pages/CalculatorPage'));
const Licences = lazy(() => import('./pages/Licences'));

// Conv-49 — the six pages the nav restructure emptied. Their sections now live on
// the page whose subject owns them; these paths stay only so an old bookmark, an
// old tour anchor or a stale link lands somewhere sensible.
//
// 🔴 THEY MUST BE ROUTES, NOT ABSENCES. The catch-all at the bottom of this file
// sends an unknown /app/* path to the MARKETING LANDING PAGE — it does not 404 —
// so a deleted route does not fail, it silently signs the reader out of the app
// they were using. That is also how Conv-31's deleted `desk` route made the
// screenshot harness save the landing page under the old page's name.
//
// Each target is where that page's DEFAULT tab went, so a bookmark lands on the
// content it was pointing at rather than merely on the right building.
const MOVED: [string, string][] = [
  ['invest', '/app/aggregation'],    // broker linking + KYC + order preflight
  ['partners', '/app/aggregation'],  // NPS, EPF, property, cross-border tax, cash-flow
  ['planning', '/app/insights'],     // projection, FIRE, capital gains, benchmark, dividends
  ['explore', '/app/markets'],       // default tab was the daily digest
  ['trackers', '/app/portfolio'],    // default tab was depreciation
  ['community', '/app/settings'],    // default tab was support
];

function PageFallback() {
  return (
    <div className="space-y-4 pt-4">
      <div className="skeleton h-8 w-48" />
      <div className="skeleton h-40 w-full" />
      <div className="skeleton h-40 w-full" />
    </div>
  );
}

// React Router preserves scroll position across route changes (it does NOT
// reset to the top automatically). This component scrolls the window (and the
// AppShell's scrollable <main>, when present) back to the top on every
// pathname change, so every page — legal docs, dashboard tabs, plans — opens
// at its heading instead of wherever the previous page was scrolled to.
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    // Most pages scroll on <main>; legal pages scroll on window. Reset both.
    const main = document.querySelector('main');
    if (main) main.scrollTop = 0;
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function RequireAuth({ children }: { children: ReactNode }) {
  const { session, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <div className="skeleton h-10 w-10 rounded-full" />
      </div>
    );
  }
  if (!session) return <Navigate to="/auth" state={{ from: location.pathname }} replace />;
  // Mandatory 2FA: an authenticated account that has not enrolled TOTP is sent
  // to the enrollment screen before it can reach any part of the app. The
  // server enforces the same gate (403 TWO_FACTOR_ENROLLMENT_REQUIRED).
  if (session.twoFactorRequired && !session.twoFactorEnrolled) {
    return <Navigate to="/enroll-2fa" replace />;
  }
  return <>{children}</>;
}

function RequireEnrollment({ children }: { children: ReactNode }) {
  const { session, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <div className="skeleton h-10 w-10 rounded-full" />
      </div>
    );
  }
  if (!session) return <Navigate to="/auth" replace />;
  if (session.twoFactorEnrolled) return <Navigate to="/app" replace />;
  return <>{children}</>;
}

function RequireFounder({ children }: { children: ReactNode }) {
  const { session, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <div className="skeleton h-10 w-10 rounded-full" />
      </div>
    );
  }
  // The server enforces every staff permission itself; this guard avoids a flash
  // of the console for an account that holds none (owner or staff role only).
  if (!session?.isFounder && !(session?.staffPermissions?.length)) return <Navigate to="/app" replace />;
  return <>{children}</>;
}

const LEGAL_SLUGS = ['privacy', 'terms', 'risk-disclosure', 'refunds', 'grievance', 'disclaimer', 'support', 'delete-account'];

export default function App() {
  return (
    <>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<Landing />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route
        path="/enroll-2fa"
        element={
          <RequireEnrollment>
            <EnrollTwoFactor />
          </RequireEnrollment>
        }
      />
      {LEGAL_SLUGS.map((slug) => (
        <Route key={slug} path={`/${slug}`} element={<LegalPage slug={slug} />} />
      ))}
      {/* ── PUBLIC SEO SURFACE ────────────────────────────────────────────────
          Free calculators and the regulatory-status page. No auth, no session,
          and deliberately ABOVE the /app/* block so nothing here can ever end up
          behind the enrollment or subscription gates.

          🔴 These paths are also emitted as static HTML by scripts/prerender-seo.mjs
          and listed in the generated sitemap. Adding a public route means adding
          it there too — `npm run check:seo` fails the build if a calculator in
          the spec table has no prerendered twin. */}
      <Route
        path="/calculators"
        element={
          <Suspense fallback={<PageFallback />}>
            <Calculators />
          </Suspense>
        }
      />
      <Route
        path="/calculators/:slug"
        element={
          <Suspense fallback={<PageFallback />}>
            <CalculatorPage />
          </Suspense>
        }
      />
      <Route
        path="/licences"
        element={
          <Suspense fallback={<PageFallback />}>
            <Licences />
          </Suspense>
        }
      />
      <Route path="/kite/callback" element={<KiteCallback />} />
      {/* Public verifier for proof-of-holdings statements (F102) — no session. */}
      <Route
        path="/verify"
        element={
          <Suspense fallback={<PageFallback />}>
            <VerifyProof />
          </Suspense>
        }
      />
      <Route
        path="/app"
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route
          index
          element={
            <Suspense fallback={<PageFallback />}>
              <Dashboard />
            </Suspense>
          }
        />
        <Route
          path="portfolio"
          element={
            <Suspense fallback={<PageFallback />}>
              <Portfolio />
            </Suspense>
          }
        />
        <Route
          path="insights"
          element={
            <Suspense fallback={<PageFallback />}>
              <Insights />
            </Suspense>
          }
        />
        {/* Conv-57 (F10) — budget & expense tracker. FREE top to bottom, so the
            rail row must stay unbadged (the mixed-page rule — here trivially,
            since there is nothing gated on the page at all). */}
        <Route
          path="budgets"
          element={
            <Suspense fallback={<PageFallback />}>
              <Budgets />
            </Suspense>
          }
        />
        <Route
          path="markets"
          element={
            <Suspense fallback={<PageFallback />}>
              <Markets />
            </Suspense>
          }
        />
        <Route
          path="learn"
          element={
            <Suspense fallback={<PageFallback />}>
              <Learn />
            </Suspense>
          }
        />
        <Route
          path="research"
          element={
            <Suspense fallback={<PageFallback />}>
              <Research />
            </Suspense>
          }
        />
        <Route
          path="household"
          element={
            <Suspense fallback={<PageFallback />}>
              <Household />
            </Suspense>
          }
        />
        <Route
          path="options"
          element={
            <Suspense fallback={<PageFallback />}>
              <Options />
            </Suspense>
          }
        />
        <Route
          path="advisory"
          element={
            <Suspense fallback={<PageFallback />}>
              <Advisory />
            </Suspense>
          }
        />
        <Route
          path="robo"
          element={
            <Suspense fallback={<PageFallback />}>
              <Robo />
            </Suspense>
          }
        />
        <Route
          path="execution"
          element={
            <Suspense fallback={<PageFallback />}>
              <Execution />
            </Suspense>
          }
        />
        <Route
          path="aggregation"
          element={
            <Suspense fallback={<PageFallback />}>
              <Aggregation />
            </Suspense>
          }
        />
        <Route
          path="wellness"
          element={
            <Suspense fallback={<PageFallback />}>
              <Wellness />
            </Suspense>
          }
        />
        <Route
          path="privacy"
          element={
            <Suspense fallback={<PageFallback />}>
              <Privacy />
            </Suspense>
          }
        />
        <Route
          path="ai"
          element={
            <Suspense fallback={<PageFallback />}>
              <Ai />
            </Suspense>
          }
        />
        <Route
          path="waiting"
          element={
            <Suspense fallback={<PageFallback />}>
              <Waiting />
            </Suspense>
          }
        />
        {/* The emptied pages. `replace` so Back does not bounce the reader
            straight into the redirect again. */}
        {MOVED.map(([from, to]) => (
          <Route key={from} path={from} element={<Navigate to={to} replace />} />
        ))}
        <Route
          path="plans"
          element={
            <Suspense fallback={<PageFallback />}>
              <Plans />
            </Suspense>
          }
        />
        <Route
          path="plan-comparison"
          element={
            <Suspense fallback={<PageFallback />}>
              <PlanComparison />
            </Suspense>
          }
        />
        <Route
          path="founder"
          element={
            <RequireFounder>
              <Suspense fallback={<PageFallback />}>
                <Founder />
              </Suspense>
            </RequireFounder>
          }
        />
        <Route
          path="settings"
          element={
            <Suspense fallback={<PageFallback />}>
              <SettingsPage />
            </Suspense>
          }
        />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}
